import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  
  // Get redirect destination from URL params (for deep links)
  const redirectTo = searchParams.get("redirect") || null;
  const returning = searchParams.get("returning") === "true";

  useEffect(() => {
    if (returning) {
      setIsReturning(true);
    }
  }, [returning]);

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/status`, { withCredentials: true });
        if (response.data.authenticated) {
          // Already logged in - redirect based on state
          navigate(redirectTo || response.data.redirect_to, { replace: true });
        }
      } catch (error) {
        // Not authenticated - stay on auth page
      }
    };
    checkAuth();
  }, [navigate, redirectTo]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/email/send-code`,
        { email },
        { withCredentials: true }
      );

      setStep("code");
      toast.success("Code sent to your email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/email/send-code`, { email }, { withCredentials: true });
      toast.success("New code sent to your email");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error("Please enter the code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/email/verify-code`,
        { 
          email, 
          code,
          is_returning: isReturning,
          redirect_to: redirectTo
        },
        { withCredentials: true }
      );
      
      toast.success(response.data.is_new_user ? "Account created!" : "Welcome back!");
      navigate(response.data.redirect_to, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Store redirect destination for after OAuth
    if (redirectTo) {
      sessionStorage.setItem("auth_redirect", redirectTo);
    }
    if (isReturning) {
      sessionStorage.setItem("auth_returning", "true");
    }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header sticky={false} />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md" data-testid="auth-card">
          <CardHeader className="text-center">
            <CardTitle className="font-manrope text-2xl">
              {isReturning ? "Welcome back" : "Get started"}
            </CardTitle>
            <CardDescription>
              {step === "email" 
                ? "Enter your email to continue"
                : "Enter the code sent to your email"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {step === "email" ? (
              <form onSubmit={handleSendCode} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="email-input"
                    className="h-12"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="send-code-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Continue with email
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground text-center">
                    We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>
                  </p>
                  <Input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    data-testid="code-input"
                    className="h-12 text-center text-lg tracking-widest"
                    maxLength={6}
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Didn't receive it? Check spam/junk or{" "}
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resending}
                      className="text-primary hover:underline disabled:opacity-50"
                      data-testid="resend-code-btn"
                    >
                      {resending ? "sending..." : "resend code"}
                    </button>
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90"
                  disabled={loading || code.length !== 6}
                  data-testid="verify-code-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  Continue
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-sm"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                  }}
                >
                  Use a different email
                </Button>
              </form>
            )}

            {step === "email" && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 rounded-full"
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                {/* Toggle between new user and returning user */}
                <div className="text-center">
                  {isReturning ? (
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setIsReturning(false)}
                    >
                      New here? <span className="text-primary">Start free assessment</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setIsReturning(true)}
                    >
                      Returning user? <span className="text-primary">Log in</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default Auth;
